<template>
  <div v-if="!showError">
    <div style="height: 100%" v-if="loadingPage">
      <loader size="xxs" :loader-image="false" />
    </div>
    <div v-else class="w-full h-full">
      <div class="w-full flex justify-end items-center mb-4">
        <div v-if="!isEditMode" class="cursor-pointer bg-dynamicBackBtn text-white px-3 py-2 font-bold flex items-center gap-2 rounded text-sm" @click="handleEditPrivilege">
          <Icon icon-name="edit" size="xs" class="text-white" style="fill: transparent;"/>
          Edit Pay Settings
        </div>
        <div v-else class="cursor-pointer bg-dynamicBackBtn text-white px-3 py-2 font-bold flex items-center gap-2 rounded text-sm" @click="cancelEdit">
          <Icon icon-name="close-icon" size="xs" class="text-white" style="fill: transparent;"/>
          Cancel Changes
        </div>
      </div>
      <div class="w-full rounded-lg p-3 bg-color" v-if="!isBasic">
        <div class="w-full flex justify-between items-center">
          <div class="flex justify-start items-center gap-1">
            <Icon icon-name="idea-icon" size="xs" />
            <p>Tips:</p>
          </div>
          <div @click="isOpenTips = !isOpenTips">
            <Icon v-if="!isOpenTips" icon-name="chevronDown" size="xs" class="cursor-pointer"/>
            <Icon v-if="isOpenTips" icon-name="chevronUp" size="xs" class="cursor-pointer"/>
          </div>
        </div>
        <p v-if="isOpenTips" class="p-4 text-justify text-jet text-xs">
          Employees automatically inherit pay settings configured under the general pay structure,
          based on the selected criteria <span class="font-bold">(Employment Type, Organisational Unit, Job Level, and Location).</span>
          However, you can choose to personalize an employee's pay settings here. By default, any changes to the general settings will <span class="font-bold">override</span> personalization's made here.
          To prevent this, simply <span class="font-bold">toggle off the override.</span> When the override is disabled, future updates to the general pay settings will <span class="font-bold">not apply</span> to this employee, and any adjustments must be made manually.
          Ensure that all <span class="font-bold">pay items contributing to the annual gross</span> collectively <span class="font-bold">total 100%</span> to maintain pay structure integrity.
        </p>
      </div>
      <div class="w-full flex justify-between items-center gap-5 mt-8">
        <div class="w-full">
          <div class="text-sm text-jet">
            Pay Group
          </div>
          <div class="relative w-full" v-if="isChangePaySetting">
            <div v-if="!isBasic && isEditMode" @click="isChangePaySetting = false" class="absolute top-3 right-3 z-20">
              <Icon
                icon-name="red-edit" size="xs"
                class="cursor-pointer"
              />
            </div>
            <div class="h-10 mt-2 capitalize" :class="'disabled'">
              {{ paySettingsName ? paySettingsName : 'Autoloaded' }}
            </div>
          </div>
          <c-select v-else placeholder="Autoloaded"
            variant="w-full h-10"
            :options="PaySettingsOptions" v-model="paySettingsName"
            :disabled="!isEditMode"
          />
        </div>
        <div class="w-full">
          <div class="text-sm text-jet">Pay Type</div>
          <c-select placeholder="--Select--" variant="w-full h-10" :options="PayTypeOptions" v-model="payType"
            @input="onChangePayType($event)" :disabled="!isEditMode"/>
        </div>
        <div class="w-full">
          <div class="text-sm text-jet">Pay Frequency</div>
          <c-select placeholder="--Select--" variant="w-full h-10" :options="payFrequencyOptions" @onChange="handleFreq"
            v-model="payScheduleId" :disabled="!isEditMode" />
          <small v-if="!PayTypeOptions.length" class="text-flame font-extrabold">
            You need to set up your pay frequencies in pay settings.
          </small>
        </div>
      </div>

      <div class="w-full mt-8 mb-8">
        <p class="font-black text-xs text-jet mb-5 uppercase">Statutory Deductions</p>
        <div v-if="isBasic || (!isBasic && employmentType !== 'Independent Contractor')" class="w-full flex justify-start items-center gap-6 text-jet font-normal text-base">
          <div class="flex gap-1 justify-start items-center">
            <SwitchToggle :value="enabledPAYE" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
              @input="!isEditMode ? '' : toggleStatutory('paye')"
            />
            <p>PAYE</p>
          </div>
          <div class="flex gap-1 justify-start items-center">
            <SwitchToggle :value="enabledPension" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
              @input="!isEditMode ? '' : toggleStatutory('pension')"
            />
            <p>Pension</p>
          </div>
          <div class="flex gap-2 justify-start items-center">
            <div class="flex gap-1 justify-start items-center">
              <SwitchToggle :value="enabledNHF" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
                @input="!isEditMode ? '' : toggleStatutory('nhf')"
              />
              <p>NHF</p>
            </div>
            <div @mouseenter="onEnter('nhf')" @mouseleave="onLeave('nhf')">
              <Icon
                icon-name="red-warning-icon"
                size="xs" class="cursor-pointer"
              />
              <ToolTip v-if="IsNHFToolTip" style="width: 298px !important" left="610px">
                <p class="tooltip__header text-white mb-1">NHF tool tip</p>
                <p class="w-full text-xs text-white text-left">
                  NHF is a voluntary health insurance contribution that you can enable for employees. Once activated,
                  employees will be automatically enrolled but may choose to opt out if they prefer not to participate.
                </p>
              </ToolTip>
            </div>
          </div>
          <div class="flex gap-2 justify-start items-center">
            <div class="flex gap-1 justify-start items-center">
              <SwitchToggle :value="enabledNhis" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
                @input="!isEditMode ? '' : toggleStatutory('nhis')"
              />
              <p>NHIS</p>
            </div>
            <div @mouseenter="onEnter('nhis')" @mouseleave="onLeave('nhis')">
              <Icon
                icon-name="red-warning-icon"
                size="xs" class="cursor-pointer"
              />
              <ToolTip v-if="IsNHISToolTip" style="width: 298px !important" left="742px">
                <p class="tooltip__header text-white mb-1">NHIS tool tip</p>
                <p class="w-full text-xs text-white text-left">
                  NHIS is a voluntary health insurance contribution that you can enable for employees.
                  Once activated, employees will be automatically enrolled but may choose to opt out if they prefer not to participate.
                </p>
              </ToolTip>
            </div>
          </div>
        </div>
        <div v-if="!isBasic && employmentType === 'Independent Contractor'"
          class="w-full flex justify-start items-center gap-6 text-jet font-normal text-base"
        >
          <div class="flex gap-1 justify-start items-center">
            <SwitchToggle :value="enabledWHT" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
              @input="!isEditMode ? '' : toggleStatutory('wht')"
            />
            <p>WHT</p>
          </div>
        </div>
      </div>
      <accordionPaySetTables title="Earnings"
        :pay-settings-data="employeeEarnings"
        :can-edit="isEditMode"
        @edit-RateValue="editRateValue"
      />
      <!-- <accordionPaySetTables v-if="employmentType !== 'Independent Contractor'"
        title="Deductions"
        :pay-settings-data="employeeDeductions"
        :can-edit="isEditMode"
        @edit-RateValue="editRateValue"
      /> -->
      <other-benefits v-if="isBasic || (!isBasic && employmentType !== 'Independent Contractor')"
        :other-benefit-data="otherBenefitData"
        :can-edit="isEditMode"
        @edit-RateValue="editOtherBenefitsRateValue"
      />
      <employeeReliefs v-if="isBasic || (!isBasic && employmentType !== 'Independent Contractor')"
        title="Reliefs"
        :employee-reliefs="employeeReliefs"
        :employee-reliefs-history="employeeReliefsHistorys"
        :employee-user-id="employeeDetails.userId"
        :is-Loading="isLoadingReliefs"
        :can-edit="isEditMode"
        @update-reliefs-status="updateReliefsStatus"
      />
      <div class="w-full mt-4 text-jet" v-if="!isBasic">
        <p class="font-black text-xs mb-2 uppercase">Data Override</p>
        <div class="flex gap-1 justify-start items-center">
          <p>Allow changes in general settings to override employees pay settings configuration</p>
          <SwitchToggle :value="allowGeneralPaySettingsOverride" :class="!isEditMode ? 'm-top opacity-50' : 'm-top opacity-100'"
            @input=" !isEditMode ? '' : toggleDataOverride()"
          />
        </div>
      </div>
      <div class="line"></div>
      <div v-if="isEditMode" class="w-full h-16 flex justify-start items-center gap-4">
        <Button
          class="text-white rounded-xl bg-dynamicBackBtn h-11 flex justify-center items-center"
          :class="isBasic ? 'w-40' : 'w-28'"
          @click="savePaySettingChanges"
          :disabled="isSaving"
        >
          {{ isBasic ? 'Save Changes' : 'Save' }}
        </Button>
        <Button
          class="text-darkPurple bg-white h-11 flex justify-center items-center"
          :class="isBasic ? 'w-48' : 'w-28'"
          @click="cancelEdit"
        >
          {{ isBasic ? 'Revert Changes' : 'Cancel' }}
        </Button>
      </div>
    </div>
  </div>
  <div v-else>
    <ErrorPage />
  </div>
</template>

<script>
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import ErrorPage from "@/modules/Admin/error403";
import accordionPaySetTables from "./accordionPaySetTables";
import employeeReliefs from "./Reliefs/index";
import otherBenefits from "./otherBenefits";

export default {
  components: {
    CSelect,
    accordionPaySetTables,
    employeeReliefs,
    otherBenefits,
    SwitchToggle: () => import('@/components/Switch'),
    ToolTip: () => import('@/components/ToolTip'),
    ErrorPage,
  },
  props: {
    employeeDetails: {
      type: Object,
      default: () => { },
    },
    otherBenefitData: {
      type: Array,
      default() {
        return [];
      }
    },
  },
  data() {
    return {
      IsNHISToolTip: false,
      showError: false,
      IsNHFToolTip: false,
      isOpenTips: true,
      loadingPage: false,
      isLoadingReliefs: false,
      payType: 'salaried',
      payFrequency: null,
      payScheduleId: null,
      paySettingsName: '',
      payFrequencyOptions: [],
      PayTypeOptions: [],
      hourlyOptions: [],
      salariedOptions: [],
      enabledNHF: false,
      enabledNhis: false,
      enabledPAYE: false,
      enabledPension: false,
      enabledWHT: false,
      employeeEarnings: [],
      employeeDeductions: [],
      employeeReliefs: [],
      employeeReliefsHistorys: [],
      allowGeneralPaySettingsOverride: false,
      PaySettingsOptions: [],
      isChangePaySetting: true,
      employeeId: '',
      paySettingOrgId: '',
      isSaving: false,
      employmentType: '',
      earningsRateValue: false,
      isEditMode: false,
      originalData: null,
    };
  },
  computed: {
    isBasic() {
      return (
        this.$store.state.subscription &&
        this.$store.state.subscription.plan === "basic"
      );
    },
  },
  methods: {
    async handleEditPrivilege() {
      try {
        await this.$handlePrivilege('employeeDirectory', 'editPaySettings');
        this.isEditMode = true;
        this.storeOriginalData();
      } catch (error) {
        this.$toasted.error('You do not have permission to edit pay settings', {
          duration: 5000
        });
        this.isEditMode = false;
      }
    },
    storeOriginalData() {
      this.originalData = {
        paySettingsName: this.paySettingsName,
        payType: this.payType,
        payScheduleId: this.payScheduleId,
        enabledPAYE: this.enabledPAYE,
        enabledPension: this.enabledPension,
        enabledNHF: this.enabledNHF,
        enabledNhis: this.enabledNhis,
        enabledWHT: this.enabledWHT,
        allowGeneralPaySettingsOverride: this.allowGeneralPaySettingsOverride,
        employeeEarnings: JSON.parse(JSON.stringify(this.employeeEarnings)),
        employeeDeductions: JSON.parse(JSON.stringify(this.employeeDeductions)),
      };
    },
    cancelEdit() {
      if (this.originalData) {
        this.paySettingsName = this.originalData.paySettingsName;
        this.payType = this.originalData.payType;
        this.payScheduleId = this.originalData.payScheduleId;
        this.enabledPAYE = this.originalData.enabledPAYE;
        this.enabledPension = this.originalData.enabledPension;
        this.enabledNHF = this.originalData.enabledNHF;
        this.enabledNhis = this.originalData.enabledNhis;
        this.enabledWHT = this.originalData.enabledWHT;
        this.allowGeneralPaySettingsOverride = this.originalData.allowGeneralPaySettingsOverride;
        this.employeeEarnings = JSON.parse(JSON.stringify(this.originalData.employeeEarnings));
        this.employeeDeductions = JSON.parse(JSON.stringify(this.originalData.employeeDeductions));
        // Reset all isEdit flags
        for (let i = 0; i < this.employeeEarnings.length; i++) {
          this.employeeEarnings[i].isEdit = false;
        }
        for (let i = 0; i < this.employeeDeductions.length; i++) {
          this.employeeDeductions[i].isEdit = false;
        }
      }
      this.isEditMode = false;
      this.originalData = null;
    },

    async updateReliefsStatus(id, newStatus) {
      const payload = {
        status: newStatus
      }
      try {
        await this.$_patchEmployeeReliefs(payload, this.employeeDetails.id, id);
        await this.getEmployeeReliefs();
        return this.$toasted.success("Employee relief updated successfully", { duration: 5000 });
      } catch (error) {
        return this.$toasted.error(error.response.data.errors[0].status, { duration: 5000 });
      }
    },
    editOtherBenefitsRateValue(i){
      if (!this.isEditMode) return;
      this.$emit('edit-RateValue', i)
    },
    editRateValue(id, title) {
      if (!this.isEditMode) return;
      const items =
        title === 'Earnings'
          ? this.employeeEarnings
          : this.employeeDeductions;

      const idx = items.findIndex(e => e.id === id);
      if (idx === -1) return;
      items[idx].isEdit = !items[idx].isEdit;
    },
    onEnter(value){
      if (value === 'nhf') {
        this.IsNHFToolTip = true;
        return
      }
      if (value === 'nhis') {
        this.IsNHISToolTip = true;
      }
    },
    onLeave(value){
       if (value === 'nhf') {
        this.IsNHFToolTip = false;
        return
      }
      if (value === 'nhis') {
        this.IsNHISToolTip = false;
      }
    },
    toggleStatutory(type) {
      switch (type) {
        case 'paye':
          this.enabledPAYE = !this.enabledPAYE;
          break;
        case 'pension':
          this.enabledPension = !this.enabledPension;
          break;
        case 'nhf':
          this.enabledNHF = !this.enabledNHF;
          break;
        case 'nhis':
          this.enabledNhis = !this.enabledNhis;
          break;
        case 'wht':
          this.enabledWHT = !this.enabledWHT;
          break;
        default:
          break;
      }
    },
    toggleDataOverride(){
        this.allowGeneralPaySettingsOverride = !this.allowGeneralPaySettingsOverride;
    },
    async getEmployeePaysettings() {
      this.loadingPage = true;
      try {
        const result = await this.$_getEmployeePaySettings({employeeId: this.employeeDetails.id });
        this.employmentType = result.data.employee.employmentType;
        this.paySettingsName = result.data.paysetting?.name;
        this.paySettingOrgId = result.data.paysetting?.id;
        const { payType, payFrequency } = this.employeeDetails;
        this.payType = payType || "salaried";
        this.payFrequency = this.getSlug(payFrequency);
        await this.getPayFrequencySetup();
        this.enabledNHF = result.data.employee.enabledNHF;
        this.enabledNhis = result.data.employee.enabledNhis;
        this.enabledPAYE = result.data.employee.enabledPAYE;
        this.enabledPension = result.data.employee.enabledPension;
        this.enabledWHT = result.data.employee.enabledWHT;
        this.employeeEarnings = result.data?.earnings.map(item => ({ ...item, isEdit: false }));
        this.employeeDeductions = result.data?.deductions.map(item => ({ ...item, isEdit: false }));
        this.employeeId = this.employeeDetails.id;
        this.allowGeneralPaySettingsOverride = result.data.employee.allowGeneralPaySettingsOverride || false;
        await this.getEmployeeReliefs();
        await this.getAllPaySettings();
        this.loadingPage = false;
      } catch (error) {
        this.loadingPage = false;
      }
    },
    async getEmployeeReliefs() {
      this.isLoadingReliefs = true;

      const current = await this.$_getEmployeeReliefs(
        { employeeId: this.employeeDetails.userId },
        'latest'
      );
      this.employeeReliefs = current.data.data;
      const history = await this.$_getEmployeeReliefs(
        { employeeId: this.employeeDetails.userId },
        'history'
      );
      this.employeeReliefsHistorys = history.data.data;
      this.isLoadingReliefs = false;
    },
    async getAllPaySettings(){
      const result = await this.$_getAllPaySettings();
      this.PaySettingsOptions = Array.isArray(result.data.paySettings)
      ? result.data.paySettings
          .filter(item => item.name !== 'Independent Contractor')
          .map(item => ({
            name: item.name,
            id: item.id
          }))
      : [];
    },
    getSlug(name) {
      if (name === "weekly") return "Four Times Monthly";
      if (name === "monthly") return "Monthly";
      if (name === "twice_monthly") return "Twice Monthly";
      return "";
    },
    handleFreq(data) {
      const findSalaried = this.payFrequencyOptions.find(
        (item) => item.id === data
      );
      if (findSalaried) {
        this.payFrequency = findSalaried.name;
      }
    },
    getFrequencyId(name) {
      const formattedName = name;
      const findId = this.payFrequencyOptions.find(
        (item) => item.name === formattedName
      );
      if (findId) this.payScheduleId = findId.id;
    },
    onChangePayType(payType) {
      if (payType === "salaried")
        this.payFrequencyOptions = this.salariedOptions;
      else if (payType === "hourly")
        this.payFrequencyOptions = this.hourlyOptions;
    },
    getPayFrequencySetup() {
      this.$_getPaySchedulesSetup()
        .then(({ data }) => {
          if (data.hourlyIsSetup)
            this.PayTypeOptions.push({ name: "Hourly", id: "hourly" });
          if (data.salariedIsSetup)
            this.PayTypeOptions.push({ name: "Salaried", id: "salaried" });
          this.hourlyOptions = data.hourly?.map((i) => ({
            ...i,
            name: this.getSlug(i.name),
          }));
          this.salariedOptions = data.salaried?.map((i) => ({
            ...i,
            name: this.getSlug(i.name),
          }));
          this.onChangePayType(this.payType);
          this.getFrequencyId(this.payFrequency);
        })
        .catch((error) => {
          const message = "An Error occured, please contact your administrator";
          this.$toasted.error(error || message, { duration: 5000 });
        });
    },
    async validateEarningRateValue(items) {
      if (!items || items.length === 0) {
        return true;
      }
      const earningsRateValue = items.filter(item =>
        item.rateType.label.includes("Earning")
      );
      const totalEarningsRateValue = earningsRateValue.reduce(
        (sum, item) => sum + Number(item.employeeSetting.rateValue || 0),
        0
      );
      const response = await this.$_validatePayItem(this.employeeId, this.paySettingOrgId)
      const { currentGross } = response.data;
      if(totalEarningsRateValue > currentGross || totalEarningsRateValue < currentGross){
        this.$toasted.error('Total Earnings Rate should not exceed 100% or set below 100%', { duration: 5000 });
        return false
      }
      this.$toasted.success("Total Earnings Rate value updated successfully", { duration: 5000 });
      return true;
    },
    async savePaySettingChanges(){
      const ok = await this.validateEarningRateValue(this.employeeEarnings);
      if(!ok){
        return
      }
      this.isSaving = true;
      const combinedEarningsDeductions = [...(this.employeeEarnings || []), ...(this.employeeDeductions || [])];
      const employeeSettings = combinedEarningsDeductions.map(item => {
          if (!item.employeeSetting) return null;
          const { employeeId, paySettingId, rateValue } = item.employeeSetting;
          return { employeeId, paySettingId, rateValue };
      }).filter(Boolean);
      const payload = {
        paysettings: [
           ...employeeSettings
        ],
        employeeId: this.employeeId,
        enabledNHF: this.enabledNHF,
        enabledNhis: this.enabledNhis,
        enabledPension: this.enabledPension,
        enabledPAYE: this.enabledPAYE,
        enabledWHT: this.enabledWHT,
        allowGeneralPaySettingsOverride: this.allowGeneralPaySettingsOverride,
        payType: this.payType,
        payFrequency: this.payFrequency,
      }
      try {
        await this.$_patchEmployeePaySettings(payload);
        this.isSaving = false;
        await this.getEmployeePaysettings();
        this.isEditMode = false;
        this.originalData = null;
        this.$toasted.success("Employee Pay setting updated successfully", { duration: 5000 });
      } catch (error) {
        this.isSaving = false;
        this.$toasted.error("Error updating employee pay setting", { duration: 5000 });
      }
    }
  },
  async mounted() {
    try {
      await this.$handlePrivilege("employeeDirectory", "viewPaySettings");
    } catch (error) {
        this.showError = true;
      }
    await this.getEmployeePaysettings();
  },
};
</script>
<style scoped>
.bg-color{
  border: 1px solid #E993235C;
  background: #E9932314;
}
.m-top{
  margin-top: 6px;
}
.tooltip__header{
    min-height: 17px;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.035em;
    border-bottom: 0.5px dashed #FFFFFF;
    margin: 4px 0px;
}
.line{
  border: 0.5px solid #00000029;
  width: 100%;
  height: 0;
  margin-top: 15px;
  margin-bottom: 5px;
  border-radius: 10px;
  padding: 0;
}
</style>
